/**
 * EventsService — Service de consultation du journal des événements (c_event)
 * 
 * Permet d'extraire les événements avec filtres multi-critères (GET /events)
 * et de relire un événement spécifique avec son payload complet (GET /pullevent/{id}).
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Entité représentant un enregistrement de c_event enrichi pour la consultation.
 */
export interface EventItem {
  id: number;
  typ: number;
  userid: number;
  user_login?: string;
  objecttyp: number;
  objectid: number;
  datcre?: string;
  datpro?: string;
  result?: number;
  data: any;
  /** État UI interne : rechargement en cours via pullevent */
  _reloading?: boolean;
  /** État UI interne : notification de copie du JSON */
  _copied?: boolean;
}

/**
 * Critères de recherche pour la consultation des événements.
 */
export interface EventsFilter {
  id?: number | string;
  limit?: string;
  objecttyp?: number | string;
  typ?: number | string;
  objectid?: number | string;
  userid?: number | string;
  datstart?: Date;
  datend?: Date;
}

/**
 * Convertit une date Date en chaîne YYYY-MM-DD locale (sans décalage UTC).
 */
function toLocalDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  constructor(private http: HttpClient) {}

  /**
   * Récupère la liste des événements filtrés depuis le backend.
   *
   * @param filter Critères de recherche (ID, objet, action, opérateur, dates, limite)
   * @returns Observable de la liste d'événements
   */
  getEvents(filter: EventsFilter): Observable<EventItem[]> {
    let params = new HttpParams();

    if (filter.id !== undefined && filter.id !== null && filter.id !== '') {
      params = params.set('id', filter.id.toString());
    }
    if (filter.limit !== undefined && filter.limit !== null && filter.limit !== '') {
      params = params.set('limit', filter.limit.toString());
    }
    if (filter.objecttyp !== undefined && filter.objecttyp !== null && filter.objecttyp !== '' && filter.objecttyp !== '-1') {
      params = params.set('objecttyp', filter.objecttyp.toString());
    }
    if (filter.typ !== undefined && filter.typ !== null && filter.typ !== '' && filter.typ !== '-1') {
      params = params.set('typ', filter.typ.toString());
    }
    if (filter.objectid !== undefined && filter.objectid !== null && filter.objectid !== '') {
      params = params.set('objectid', filter.objectid.toString());
    }
    if (filter.userid !== undefined && filter.userid !== null && filter.userid !== '' && filter.userid !== '-1') {
      params = params.set('userid', filter.userid.toString());
    }
    if (filter.datstart instanceof Date) {
      params = params.set('datstart', toLocalDay(filter.datstart));
    }
    if (filter.datend instanceof Date) {
      params = params.set('datend', toLocalDay(filter.datend));
    }

    return this.http.get<EventItem[]>(`${environment.server_url}/events`, { params });
  }

  /**
   * Relit un événement unique par son identifiant via l'endpoint dédié /pullevent/{id}.
   * Utilisé pour rafraîchir à la demande un payload ou charger un événement précis.
   *
   * @param id Identifiant primaire de l'événement (c_event.id)
   * @returns Observable de l'événement rechargé
   */
  pullEvent(id: number): Observable<EventItem> {
    return this.http.get<EventItem>(`${environment.server_url}/pullevent/${id}`);
  }
}
